/**
 * React Hook for Search Activity Logging
 * Provides easy access to search logging functionality throughout the app
 */

import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import searchLogger from '../services/searchLogger';

export const useSearchLogger = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Helper to get auth token
  const getAuthToken = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      console.warn('Could not get auth token for logging:', error);
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Generic activity logger
  const logActivity = useCallback(async (activityData) => {
    const authToken = await getAuthToken();
    return searchLogger.logActivity(activityData, authToken);
  }, [getAuthToken]);

  // Load search logging
  const logLoadSearch = useCallback(async (searchFilters, resultsCount = 0, responseTimeMs = 0) => {
    const authToken = await getAuthToken();
    return searchLogger.logLoadSearch(searchFilters, resultsCount, responseTimeMs, authToken);
  }, [getAuthToken]);

  // Broker metrics logging
  const logBrokerMetrics = useCallback(async (filters, resultsCount = 0, responseTimeMs = 0) => {
    const authToken = await getAuthToken();
    return searchLogger.logBrokerMetrics(filters, resultsCount, responseTimeMs, authToken);
  }, [getAuthToken]);

  // Rate insights logging
  const logRateInsights = useCallback(async (filters, resultsCount = 0, responseTimeMs = 0) => {
    const authToken = await getAuthToken();
    return searchLogger.logRateInsights(filters, resultsCount, responseTimeMs, authToken);
  }, [getAuthToken]);

  // Lane pricing logging
  const logLanePricing = useCallback(async (searchData, resultsCount = 0, responseTimeMs = 0) => {
    const authToken = await getAuthToken();
    return searchLogger.logLanePricing(searchData, resultsCount, responseTimeMs, authToken);
  }, [getAuthToken]);

  // Map activity logging
  const logMapActivity = useCallback(async (activityType, mapData = {}) => {
    const authToken = await getAuthToken();
    return searchLogger.logMapActivity(activityType, mapData, authToken);
  }, [getAuthToken]);

  // City click logging
  const logCityClick = useCallback(async (cityData) => {
    const authToken = await getAuthToken();
    return searchLogger.logCityClick(cityData, authToken);
  }, [getAuthToken]);

  // Route generation logging
  const logRouteGeneration = useCallback(async (routeData) => {
    const authToken = await getAuthToken();
    return searchLogger.logRouteGeneration(routeData, authToken);
  }, [getAuthToken]);

  // Filter change logging
  const logFilterChange = useCallback(async (functionPage, filterName, oldValue, newValue) => {
    const authToken = await getAuthToken();
    return searchLogger.logFilterChange(functionPage, filterName, oldValue, newValue, authToken);
  }, [getAuthToken]);

  // Table sort logging
  const logTableSort = useCallback(async (functionPage, sortColumn, sortDirection) => {
    const authToken = await getAuthToken();
    return searchLogger.logTableSort(functionPage, sortColumn, sortDirection, authToken);
  }, [getAuthToken]);

  // Data export logging
  const logDataExport = useCallback(async (functionPage, exportType, recordCount) => {
    const authToken = await getAuthToken();
    return searchLogger.logDataExport(functionPage, exportType, recordCount, authToken);
  }, [getAuthToken]);

  // Performance wrapper - times execution and logs automatically
  const withLogging = useCallback((functionPage, activityType = 'search') => {
    return async (asyncFunction, additionalData = {}) => {
      const startTime = performance.now();
      let resultsCount = 0;
      let error = null;

      try {
        const result = await asyncFunction();
        
        // Try to extract results count from common result formats
        if (Array.isArray(result)) {
          resultsCount = result.length;
        } else if (result && typeof result === 'object') {
          resultsCount = result.length || result.data?.length || result.results?.length || 
                        result.total_records || result.brokers?.length || 0;
        }

        return result;
      } catch (err) {
        error = err;
        throw err;
      } finally {
        const endTime = performance.now();
        const responseTimeMs = Math.round(endTime - startTime);

        // Log the activity
        try {
          await logActivity({
            functionPage,
            activityType,
            searchParameters: additionalData.searchParameters || {},
            filtersApplied: additionalData.filtersApplied || {},
            resultsCount,
            responseTimeMs,
            ...additionalData,
            ...(error && { error: error.message })
          });
        } catch (loggingError) {
          console.warn('Failed to log activity:', loggingError);
        }
      }
    };
  }, [logActivity]);

  return {
    logActivity,
    logLoadSearch,
    logBrokerMetrics,
    logRateInsights,
    logLanePricing,
    logMapActivity,
    logCityClick,
    logRouteGeneration,
    logFilterChange,
    logTableSort,
    logDataExport,
    withLogging,
    isAuthenticated
  };
};

export default useSearchLogger;